import { DateTime } from 'luxon';
import { SiteTimelineStep } from '../../generated/graphql';

export const getFixedTimeBoundary = (
  fixedTime: string,
  now: DateTime = DateTime.now(),
) => {
  const fixedTimeParts = fixedTime.split(':');
  const fixedHour = parseInt(fixedTimeParts[0]);
  const fixedMinute = parseInt(fixedTimeParts[1]);

  let fixedTimeBoundary = now.set({
    hour: fixedHour,
    minute: fixedMinute,
    second: 0,
    millisecond: 0,
  });
  if (fixedTimeBoundary <= now) {
    fixedTimeBoundary = fixedTimeBoundary.plus({ days: 1 });
  }
  return fixedTimeBoundary;
};

export type TotalSessionDurationInput = {
  now: DateTime;
  timelineStep: SiteTimelineStep;
  hasTopupTimeline: boolean;
  topupAnchorFixedTime?: string | null;
};

/**
 * Total session minutes from `now` until paid-until for a prepay timeline step.
 * Used for both on-screen paid-until display and the `duration` payment field.
 */
export const getTotalSessionDurationMinutes = ({
  now,
  timelineStep,
  hasTopupTimeline,
  topupAnchorFixedTime,
}: TotalSessionDurationInput): number => {
  const stepMinutes = timelineStep.durationMinutes ?? 0;

  if (timelineStep.fixedTime) {
    const fixedTimeBoundary = getFixedTimeBoundary(
      timelineStep.fixedTime,
      now,
    );
    const timeToCompare =
      hasTopupTimeline && topupAnchorFixedTime
        ? getFixedTimeBoundary(topupAnchorFixedTime, now)
        : now;
    const minutesToBoundary =
      hasTopupTimeline && !timelineStep.specialTariffId
        ? 0
        : fixedTimeBoundary.diff(timeToCompare, 'minutes').minutes;
    return Math.ceil(minutesToBoundary + stepMinutes);
  }

  return Math.round(stepMinutes);
};

export type SessionStartInput = {
  now: DateTime;
  hasTopupTimeline: boolean;
  topupAnchorFixedTime?: string | null;
};

/**
 * Where the purchased time actually starts.
 *
 * A topup is chained onto the end of the active ticket by the API — see
 * `newBoughtAt = activeTicket.untilTime + 1` in the API's
 * parking-ticket.service.ts, with `untilTime = boughtAt + duration * 60`. So
 * paid-until has to be measured from that anchor, not from now, or the display
 * under-reports the end of the session by however long the active ticket has
 * left to run. A fresh ticket starts now.
 */
export const getSessionStart = ({
  now,
  hasTopupTimeline,
  topupAnchorFixedTime,
}: SessionStartInput): DateTime =>
  hasTopupTimeline && topupAnchorFixedTime
    ? getFixedTimeBoundary(topupAnchorFixedTime, now)
    : now;

export const getTimelineSessionTiming = (
  input: TotalSessionDurationInput,
): {
  totalMinutes: number;
  startTime: DateTime;
  paidUntilTime: DateTime;
} => {
  // totalMinutes stays measured the way the API expects it for `duration`;
  // only the start of the window differs between a topup and a fresh ticket.
  const totalMinutes = getTotalSessionDurationMinutes(input);
  const startTime = getSessionStart(input);
  return {
    totalMinutes,
    startTime,
    paidUntilTime: startTime.plus({ minutes: totalMinutes }),
  };
};

const DURATION_INVARIANT_TOLERANCE_MINUTES = 1;

/** Dev-only guard: paid-until display must match total minutes sent in `duration`. */
export const logPaymentDurationMismatch = (params: {
  totalMinutes: number;
  paidUntilTime: DateTime | null;
  now: DateTime;
  /** Start of the paid window — the topup anchor when chaining, else now. */
  startTime?: DateTime;
  timelineIndex?: number;
  currentTimelineStep?: SiteTimelineStep | null;
  durationState?: number;
  timelineStepDurationMinutes?: number;
}): void => {
  if (process.env.NODE_ENV === 'production') {
    return;
  }

  const { totalMinutes, paidUntilTime, now, startTime, ...rest } = params;
  if (!paidUntilTime) {
    return;
  }

  const displayedMinutes = paidUntilTime.diff(
    startTime ?? now,
    'minutes',
  ).minutes;
  if (
    Math.abs(totalMinutes - displayedMinutes) >
    DURATION_INVARIANT_TOLERANCE_MINUTES
  ) {
    console.error(
      '[payment] Duration mismatch: total minutes vs paid-until display',
      {
        totalMinutes,
        displayedMinutes,
        ...rest,
      },
    );
  }
};
